// Libraries
import React from 'react';
import { NavLink } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { rem } from 'polished';

// Components
import IsAuthenticated from '~/components/general/restricted/is-authenticated/index';
import IsAdmin from '~/components/general/restricted/is-admin/index';

// Constants
import {
	INDEX_URL,
	CHALLENGES_URL,
	MY_PROFILE_URL,
	LOGIN_URL,
	ADMIN_CHALLENGES,
	// ADMIN_DONATIONS_AND_REWARDS,
	// ADMIN_PLAYERS,
} from '~/data/constants/router-constants';
import { IconAlert } from '../../layout/icons';
import { BLOG_URL } from '~/data/constants/router-constants';
import { FAQS_URL } from '~/data/constants/router-constants';

const MobileMenu = props => {
	const renderIconAlert = () => {
		if (props.hasIconAlert) {
			return (
				<div className="alert-icon">
					<IconAlert tooltipLeft={false} bgColor="white" invertColors />
				</div>
			);
		}
		return null;
	};
	return (
		<Wrapper id="mobile-menu" className={`${props.className}`}>
			<ul className="mobile-menu__list">
				<li className="mobile-menu__item">
					<NavLink
						exact={true}
						to={INDEX_URL}
						title="The Run 4 a Child Initiative"
						activeStyle={{
							color: 'rgb(124, 131, 138)',
							backgroundColor: 'white',
						}}
					>
						Initiative
					</NavLink>
				</li>

				<IsAdmin
					isTrue={
						<li className="mobile-menu__item">
							<NavLink
								to={ADMIN_CHALLENGES}
								title="Manage Challenges"
								activeStyle={{
									color: 'rgb(124, 131, 138)',
									backgroundColor: 'white',
								}}
							>
								Manage Challenges
							</NavLink>
						</li>
					}
					isFalse={null}
				/>

				<IsAdmin
					isTrue={null}
					isFalse={
						<li className="mobile-menu__item">
							<NavLink
								to={CHALLENGES_URL}
								title="All Challenges"
								activeStyle={{
									color: 'rgb(124, 131, 138)',
									backgroundColor: 'white',
								}}
							>
								Challenges
							</NavLink>
						</li>
					}
				/>
				<li className="mobile-menu__item">
					<NavLink
						exact={false}
						to={BLOG_URL}
						title="Blog"
						activeStyle={{
							color: 'rgb(124, 131, 138)',
							backgroundColor: 'white',
						}}
					>
						Blog
					</NavLink>
				</li>
				<li className="mobile-menu__item">
					<NavLink
						exact={false}
						to={FAQS_URL}
						title="FAQs"
						activeStyle={{
							color: 'rgb(124, 131, 138)',
							backgroundColor: 'white',
						}}
					>
						FAQs
					</NavLink>
				</li>

				<IsAuthenticated
					isTrue={
						<li className="mobile-menu__item">
							<NavLink
								to={MY_PROFILE_URL}
								title="Profile"
								activeStyle={{
									color: 'rgb(124, 131, 138)',
									backgroundColor: 'white',
								}}
							>
								Profile&nbsp;&nbsp;
								{renderIconAlert()}
							</NavLink>
						</li>
					}
					isFalse={
						<li className="mobile-menu__item">
							<NavLink
								to={LOGIN_URL}
								title="Sign-in to Run 4 a Child"
								activeStyle={{
									color: 'rgb(124, 131, 138)',
									backgroundColor: 'white',
								}}
							>
								Sign-In
							</NavLink>
						</li>
					}
				/>
			</ul>
			<div className="mobile-menu__background" />
		</Wrapper>
	);
};

const MobileMenuAnimation = keyframes`
  from{
    transform: translateY(-100%);
  }

  to {
    transform: translateY(0);
  }
`;

const fadeInMenuItem = keyframes`
  from{
    opacity: 0;
    transform: translateY(-64px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInBackground = keyframes`
  from{
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

const Wrapper = styled.nav`
	display: none;
	width: 100%;
	height: 100%;
	margin: 0;
	padding: 0;
	background-color: ${props => props.theme.primaryColor};
	z-index: 0;
	position: relative;

	@media ${props => props.theme.breakpointLarge} {
		display: none !important;
	}

	&.is-open  {
		display: flex;
		height: auto;
		animation-name: ${MobileMenuAnimation};
		animation-duration: 500ms;
		animation-delay: 500ms;
		animation-timing-function: ${props => props.theme.easeOutExpo};

		li.mobile-menu__item {
			display: flex;
			animation-name: ${fadeInMenuItem};
			animation-duration: 1000ms;
			animation-timing-function: ${props => props.theme.easeOutCirc};
			animation-fill-mode: forwards;
			opacity: 0;
			height: ${rem('64px')};
		}
	}

	.mobile-menu {
		&__list  {
			margin-top: 0;
			margin-left: 0;
			margin-right: 0;
			margin-bottom: 0;
			width: 100%;
			list-style-type: none;
			display: flex;
			flex-direction: column;
			justify-content: flex-start;
		}

		&__item {
			display: none;
			opacity: 1;
			flex-direction: row;
			align-items: center;
			width: 100%;
			height: ${rem('64px')};
			background-color: ${props => props.theme.primaryColor};
			border-bottom: 1px solid #0061c5;
			flex: 1;

			&:nth-of-type(1) {
				z-index: 3;
				animation-delay: 500ms;
			}

			&:nth-of-type(2) {
				z-index: 2;
				animation-delay: 625ms;
			}

			&:nth-of-type(3) {
				z-index: 1;
				animation-delay: 750ms;
			}
			&:nth-of-type(4) {
				z-index: 1;
				animation-delay: 875ms;
			}
			&:nth-of-type(5) {
				z-index: 1;
				animation-delay: 1000ms;
			}

			a {
				padding: ${rem('16px')};
				display: flex;
				align-items: center;
				width: 100%;
				color: ${props => props.theme.white};
				font-size: ${rem('22px')};
				font-weight: 300;
				.alert-icon {
					display: flex;
				}
			}
		}

		&__background {
			display: flex;
			position: absolute;
			top: 0;
			right: 0;
			bottom: 0;
			left: 0;
			background-color: hsla(0, 0%, 0%, 0.6);
			height: ${props => `calc(100vh - ${props.theme.navbarHeightSmall})`};
			animation-name: ${fadeInBackground};
			animation-duration: 500ms;
			animation-timing-function: ${props => props.theme.easeOutCirc};
			animation-delay: 0;
			animation-fill-mode: forwards;
			opacity: 0;
			-webkit-backdrop-filter: blur(4px);
		}
	}
`;

export default MobileMenu;



// WEBPACK FOOTER //
// ./src/components/ui/navigation/header/mobile-menu.jsx